import { selectTicketSalePageData } from 'app/pages/TicketSalePage/slice/selectors';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { selectVendorFeatures } from 'store/vendorSlice/selectors';
import { isRenderedInsideEmbed } from 'utils/isRenderedInsideEmbed';
import { EVENT_TYPE_WITH_CALENDAR } from 'utils/static/constants';
import TicketTotal from '../TicketSalePageTotal';
import ProceedToCheckoutButton from '../TicketSalePageTotal/ProceedToCheckoutButton';
import TicketSection from '../TicketSection';
import TicketGroup from '../TicketSection/TicketGroup';
import TicketTypeBundleSection from '../TicketTypeBundleSection';
import ApplyCodeSection from './ApplyCodeSection';
import CalendarContainer from './Calendar/CalendarContainer';
import CalendarSaleCardSkeleton from './CalendarSaleCardSkeleton';
import { getItemsTree, isTicketType, isTicketTypeBundle } from './getItemsTree';
import ReserveSeatsCardSkeleton from './ReserveSeatsCardSkeleton';
import ScrollDownIndicator from './ScrollDownIndicator';
import TicketSaleCardHeader from './TicketSaleCardHeader';
import TicketSaleCardSkeleton from './TicketSaleCardSkeleton';

interface TicketSaleCardProps {
  handleOpenChart?: () => void;
}

const TicketSaleCard: React.FC<TicketSaleCardProps> = ({ handleOpenChart }) => {
  const { t } = useTranslation();
  const {
    loading: ticketSalePageIsLoading,
    selectedEvent: {
      type,
      tickets,
      ticketTypeBundles,
      ticketTypeGroups,
      numberOfPublicTicketTypes,
      numberOfPrivateTicketTypes,
      name,
      location,
      dateTimeFormat,
      timeInterval,
      calendar,
      seatingChart,
    },
  } = useSelector(selectTicketSalePageData);
  const { hasIdMeImplementation } = useSelector(selectVendorFeatures);
  const { pathname } = useLocation();

  if (ticketSalePageIsLoading) {
    switch (true) {
      case pathname.includes('/reserve-seats'):
        return <ReserveSeatsCardSkeleton />;
      case pathname.includes('/calendar'):
        return <CalendarSaleCardSkeleton />;
      default:
        return <TicketSaleCardSkeleton />;
    }
  }

  const thereAreAvailableTickets =
    numberOfPublicTicketTypes > 0 || numberOfPrivateTicketTypes > 0;

  return (
    <div
      className={`w-full mx-auto ${
        isRenderedInsideEmbed
          ? ''
          : 'border shadow-md rounded-lg overflow-hidden'
      } md:max-w-[656px] h-full flex flex-col`}
    >
      <TicketSaleCardHeader
        name={name}
        location={location}
        dateTimeFormat={dateTimeFormat}
        timeInterval={timeInterval}
      />
      <div
        className="h-full flex flex-col"
        style={{ background: 'var(--cardContentBackground)' }}
      >
        {thereAreAvailableTickets ? (
          <>
            {type === EVENT_TYPE_WITH_CALENDAR ? null : (
              <div
                className={`${
                  hasIdMeImplementation ? 'p-3 sm:px-4' : 'p-3 sm:p-4'
                } border-b border-neutral-40`}
              >
                <ApplyCodeSection />
              </div>
            )}
            <div className="relative">
              <div
                id="ticket-categories-container"
                className={`${seatingChart ? 'max-h-80 overflow-scroll' : ''}`}
              >
                {calendar ? (
                  <CalendarContainer />
                ) : tickets.length ? (
                  getItemsTree(
                    tickets,
                    ticketTypeBundles || [],
                    ticketTypeGroups,
                  ).map(item =>
                    isTicketType(item) ? (
                      <TicketSection
                        key={item.id}
                        ticket={item}
                        handleOpenChart={handleOpenChart}
                      />
                    ) : isTicketTypeBundle(item) ? (
                      <TicketTypeBundleSection
                        key={item.id}
                        ticketTypeBundle={item}
                      />
                    ) : (
                      <TicketGroup
                        key={item.id}
                        tickets={tickets}
                        bundles={ticketTypeBundles || []}
                        id={item.id}
                        name={item.name}
                        handleOpenChart={handleOpenChart}
                      />
                    ),
                  )
                ) : null}
              </div>
              {seatingChart && tickets.length ? <ScrollDownIndicator /> : null}
            </div>
          </>
        ) : (
          <h2 className="text-neutral-1000 p-[24px] border-b border-neutral-40">
            {t('ticketSale.thereAreNotTicketsAvailable')}
          </h2>
        )}
        {seatingChart ? null : <TicketTotal />}
        <div
          className="mt-auto p-[12px] sm:py-[16px] sm:px-[24px] flex justify-end"
          style={{ background: 'var(--cardFooterBackground)' }}
        >
          <ProceedToCheckoutButton />
        </div>
      </div>
    </div>
  );
};

export default memo(TicketSaleCard);
